/* eslint-disable */

import { Video, formattedTitleForVideo } from "@/types/Video";
import _Vue from "vue"
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { decode } from "html-entities";
import { ID3Tag, NielsenID3Tag, areID3TagsDifferent, formattedTitleForTag, parseID3TagForNielsen } from "@/types/ID3Tag";
import { formatDateForNielsen } from "@/helpers/date-formatter";

dayjs.extend(customParseFormat);

import { TVSystem } from "@/enums/tv-system";
import { CurrentLiveStream } from "@/types/cms";


class NielsenPlugin {
  public install(Vue: typeof _Vue) {
   
    this.log("Loading Nieslsen Plugin");
    (function(e,n){
      function t(e){
        return"object"==typeof e?JSON.parse(JSON.stringify(e)):e
      }
      e[n]=e[n]||
      {
        nlsQ:function(o,r,c){
          var s=e.document,
          a=s.createElement("script");
          a.async=true,
          a.src=("http:"===e.location.protocol?"http:":"https:")+"//cdn-gl.nmrodam.com/conf/"+o+".js#name="+r+"&ns="+n;
          var i=s.getElementsByTagName("script")[0];
          return i.parentNode?.insertBefore(a,i),
          e[n][r]=e[n][r]||{g:c||{},
          ggPM:function(o,c,s,a,i){e[n][r].q=e[n][r].q||[];try{var l=t([o,c,s,a,i]);e[n][r].q.push(l)}catch(e){console&&console.log&&console.log("Error: Cannot register event in Nielsen SDK queue.")}},
          trackEvent:function(o){e[n][r].te=e[n][r].te||[];try{var c=t(o);e[n][r].te.push(c)}catch(e){console&&console.log&&console.log("Error: Cannot register event in Nielsen SDK queue.")}}},
          e[n][r]
        }
      }
    })(window,"NOLBUNDLE");

    Vue.prototype.$nielsen = this
  }
  
  private getSDKInstance(system: TVSystem): any {

    if (window._nSdkInstance) {
      return window._nSdkInstance
    }

    let appId: string
    switch (system) {
      case TVSystem.LG:
        appId = 'PBCE5DBB6-C728-4630-BEEB-B15A38C9B9D8'
        break;
      case TVSystem.SAMSUNG:
        appId = 'P91186CE1-B69C-434D-95CD-378645EA381A'
        break;
      case TVSystem.OTHER:
      case TVSystem.VIRGIN_MEDIA:
      case TVSystem.TIVO:
      case TVSystem.VESTEL:
        appId = 'P6D858F07-8A69-400A-98B4-25FB5F950458'
        break;
    }

    // @ts-ignore
    // var nSdkInstance = window.NOLBUNDLE.nlsQ(appId, { nol_sdkDebug: "DEBUG" });
    var nSdkInstance = window.NOLBUNDLE.nlsQ(appId);

    window._nSdkInstance = nSdkInstance
    this.log("SDK Loaded - ", system);

    return nSdkInstance
  }

  private currentPosition: number = -1
  private hasStartedPlayback: boolean = false
  private hasMarkedEndOfStream: boolean = false
  private isSeeking: boolean = false

  // For live stream position tracking
  private latestTagDate: Date | undefined = undefined
  private latestTagPlaybackPosition: number = 0

  public trackVideo(system: TVSystem, video: Video, player: HTMLVideoElement, country_code?: string | null) {

    //If we go from playing one video to another ensure we reset state and mark end
    this.markEndOfStreamIfNeeded()
    this.currentPosition = -1
    this.hasStartedPlayback = false
    this.hasMarkedEndOfStream = false

    try {
      const nSdkInstance = this.getSDKInstance(system)


      let dateMoment = dayjs(video.custom_fields.date, 'DD.MM.YY')

      var content_metadata_object = {

        // SDK Metadata
        type:           'content',
        assetid:        video.custom_fields.p_prodcode || video.id,
        program:        decode(formattedTitleForVideo(video)).slice(0, 100),
        title:          decode(video.custom_fields.title || "").slice(0, 100),
        length:         `${Math.round(video.duration / 1000)}`, // length in seconds (String) 
        airdate:        dateMoment.format("YYYYMMDD [00:00:00]"),
        isfullepisode:  'y',
        adloadtype:     '2',
        segB:           country_code || '',
        segC:           'TG4 Player', // optional
      
      }

      this.log("Loading Nielsen: ", content_metadata_object)
      nSdkInstance.ggPM("loadMetadata", content_metadata_object); 
      
      player.addEventListener('timeupdate', () => {
        this.hasStartedPlayback = true
        let newSeconds = Math.floor(player.currentTime)
        if (this.currentPosition !== newSeconds && !player.paused) {
          this.isSeeking = false
          this.currentPosition = newSeconds
          this.log("NIELSEN: Position: ", newSeconds)
          nSdkInstance.ggPM("setPlayheadPosition", newSeconds); 
        }
      })

      player.addEventListener("pause", () => {
        this.log("NIELSEN: STOP - Pause")
        nSdkInstance.ggPM("stop", this.currentPosition);
      });

      player.addEventListener("seeking", () => {
        if (this.hasStartedPlayback && !player.paused && !this.isSeeking) {
          this.isSeeking = true
          this.log("NIELSEN: STOP - Seeking")
          nSdkInstance.ggPM("stop", this.currentPosition);
        }
      });


      player.addEventListener("ended", () => {
        this.markEndOfStreamIfNeeded()
      });
      
      player.onerror = (event) => {
        this.markEndOfStreamIfNeeded()
      }

      window.addEventListener("beforeunload", () => {
        this.markEndOfStreamIfNeeded()
      });


    } catch (error) {
      console.error(error)
      this.markEndOfStreamIfNeeded()
    }
  }

  public trackLiveEvent(system: TVSystem, player: HTMLVideoElement, stream: CurrentLiveStream, country_code?: string | null) {

    this.markEndOfStreamIfNeeded()
    this.currentPosition = -1
    this.hasStartedPlayback = false
    this.hasMarkedEndOfStream = false

    try {
      const nSdkInstance = this.getSDKInstance(system)

      const currentShow = stream.currentShow
      const assetId = currentShow?.pcode || stream.streamId
      const title = decode(currentShow?.title || '')
      const seriesTitle = decode(currentShow?.seriesTitle || '')

      if (!assetId) {
        this.log("No asset ID available for live event, skipping Nielsen tracking")
        return
      }

      let duration = 0
      let airDateFormatted = dayjs().utc().format('YYYYMMDD HH:mm:ss')
      if (currentShow?.startTime && currentShow?.endTime) {
        const start = new Date(currentShow.startTime)
        const end = new Date(currentShow.endTime)
        duration = Math.round((end.getTime() - start.getTime()) / 1000)
        airDateFormatted = formatDateForNielsen(start)
      }

      const content_metadata_object = {
        type:           'content',
        assetid:        assetId,
        program:        seriesTitle.slice(0, 100),
        title:          title.slice(0, 100),
        length:         `${duration || 86400}`,
        airdate:        airDateFormatted,
        isfullepisode:  'y',
        adloadtype:     '1',
        segB:           country_code || '',
        segC:           'TG4 Player',
      }

      player.addEventListener('timeupdate', () => {
        if (!this.hasStartedPlayback) {
          this.hasStartedPlayback = true
          this.log("NIELSEN LIVE EVENT: PLAY")
          nSdkInstance.ggPM("loadMetadata", content_metadata_object)
        }
        const currentPositionAsDate = Math.floor(Date.now() / 1000)
        if (this.currentPosition !== currentPositionAsDate && !player.paused) {
          this.currentPosition = currentPositionAsDate
          this.log("NIELSEN LIVE EVENT: Position: ", currentPositionAsDate)
          nSdkInstance.ggPM("setPlayheadPosition", currentPositionAsDate)
        }
      })

      player.addEventListener("pause", () => {
        if (this.hasStartedPlayback) {
          this.log("NIELSEN LIVE EVENT: STOP - Pause")
          nSdkInstance.ggPM("stop", this.currentPosition)
        }
      })

      player.addEventListener("ended", () => {
        this.markEndOfStreamIfNeeded()
      })

      player.onerror = () => {
        this.markEndOfStreamIfNeeded()
      }

      window.addEventListener("beforeunload", () => {
        this.markEndOfStreamIfNeeded()
      })

      this.log("Did finish setting up live event tracking", content_metadata_object)

    } catch (error) {
      console.error(error)
      this.markEndOfStreamIfNeeded()
    }
  }

  public markEndOfStreamIfNeeded() {
    try {
      // For live: check if we have a current tag; for VOD: check hasStartedPlayback
      const hasInitiatedPlayback = this.currentID3Tag != null || this.hasStartedPlayback
      if (hasInitiatedPlayback && !this.hasMarkedEndOfStream) {
        this.log("NIELSEN: END - Marking end of stream")
        //We assume we have instance of SDK
        const nSdkInstance = window._nSdkInstance
        nSdkInstance?.ggPM("end", this.currentPosition);
        this.hasMarkedEndOfStream = true
      }
    } catch (error) {
      console.error(error)
    }
  }



  private currentID3Tag: ID3Tag| undefined = undefined
  private isUserInIreland: boolean = false

  public setupTrackingForLive(system: TVSystem, player: HTMLVideoElement, isUserInIreland: boolean) {
    
    this.isUserInIreland = isUserInIreland
    
    //If we go from playing one video to another ensure we reset state and mark end
    this.markEndOfStreamIfNeeded()
    this.currentPosition = -1
    this.hasStartedPlayback = false
    this.hasMarkedEndOfStream = false

    try {

      const nSdkInstance = this.getSDKInstance(system)

      player.addEventListener('pause', () => {
        if (this.currentID3Tag != null) {
          this.log("Event: Stop")
          this.sendStopForLive(system)
        }
      })

      player.addEventListener('timeupdate', () => {
        let newSeconds = Math.floor(player.currentTime || 0)
        if (this.currentID3Tag != null) {
          if (this.currentPosition !== newSeconds && !player.paused) {
            this.currentPosition = newSeconds

            // Calculate position relative to tag creation date
            const differenceSinceTag = this.currentPosition - this.latestTagPlaybackPosition
            const currentPositionAsDate = Math.floor((this.latestTagDate?.getTime() || 0) / 1000) + differenceSinceTag

            this.log("NIELSENLIVE: Position: ", currentPositionAsDate, " (Tag date: ", this.latestTagDate, ", Diff: ", differenceSinceTag, ")")
            nSdkInstance.ggPM("setPlayheadPosition", currentPositionAsDate);
          }
        } else {
          this.currentPosition = newSeconds
        }
      })

      player.addEventListener('ended', () => {
        this.markEndOfStreamIfNeeded()
      })

      player.onerror = () => {
        this.markEndOfStreamIfNeeded()
      }

      window.addEventListener("beforeunload", () => {
        this.markEndOfStreamIfNeeded()
      });

      this.log("Did finish setting up live listeners")

    } catch (error) {
      console.error(error)
      this.markEndOfStreamIfNeeded()
    }
  }


  //Set current tag - If different stop and start metadata
  //If IOI and we are WW don't start
  public didPassID3Tag(system: TVSystem, id3Tag: ID3Tag, country_code?: string | null) {

    //Check if tags are different
    if (areID3TagsDifferent(this.currentID3Tag, id3Tag)) {

        //Only parse ID3 tag (Date calculation if Tag changed)
        const nielsenID3Tag = parseID3TagForNielsen(id3Tag);

        // Record tag timestamp and position for New tag
        this.latestTagDate = nielsenID3Tag.tagCreationDate
        this.latestTagPlaybackPosition = this.currentPosition

        this.log(`NEWPCODE: ${id3Tag.pcode}`)

        if (!this.isValidTag(id3Tag)) {
          this.log(`PCODE Ignoring invalid tag: ${id3Tag.pcode}`)
          return  // Early return - do not process invalid tags
        }

        this.log(`PCODE change: ${this.currentID3Tag?.pcode} -> ${id3Tag.pcode}`)

        // Handle program change
        if (this.currentID3Tag) {
          // Had a previous tag - this is a program change
          this.log("Event STOP")
          this.sendStopForLive(system)
        } else {
          // First valid tag - initiate playback
          this.log("Event PLAY")
          //Nielsen SDK marks as playing based on playhead updates
        }

        // Set as current tag (only valid tags reach here)
        this.currentID3Tag = id3Tag

        // Load metadata for valid tag
        this.log(`EVENT: LOADMETADATA: ${id3Tag.series_title}`)
        this.sendMetadataForLive(system, nielsenID3Tag, country_code)
    } else {
      this.log("Passed tag but same")
    }
  }

  private isValidTag(tag: ID3Tag): boolean {

    // Validate pcode first (must exist and not be "none" or "false")
    if (!tag.pcode ||
        tag.pcode === "" ||
        tag.pcode.toLowerCase() === "none" ||
        tag.pcode.toLowerCase() === "false") {
      this.log(`Invalid ID3 Tag based on pCode - ${tag.pcode}`)
      return false
    }

    // Check geographic restrictions
    return tag.ww?.toLowerCase() === "true" || this.isUserInIreland
  }

  private sendStopForLive(system: TVSystem) {

    try {
      const nSdkInstance = this.getSDKInstance(system)
      const differenceSinceTag = this.currentPosition - this.latestTagPlaybackPosition
      const currentPositionAsDate = Math.floor((this.latestTagDate?.getTime() || 0) / 1000) + differenceSinceTag

      this.log("NIELSENLIVE: STOP - At position: ", currentPositionAsDate)
      nSdkInstance.ggPM("stop", currentPositionAsDate);
    } catch(err) {
      console.error(err)
    }
  }

  private sendMetadataForLive(system: TVSystem, id3Tag: NielsenID3Tag, country_code?: string | null) {

    try {
      const nSdkInstance = this.getSDKInstance(system)

      // Use parsed startDate from ID3Tag (already parsed with Europe/Dublin timezone)
      let airDateFormatted = dayjs().utc().format('YYYYMMDD HH:mm:ss') // default fallback
      if (id3Tag.startDate) {
        airDateFormatted = formatDateForNielsen(id3Tag.startDate)
      } else {
        console.warn("No startDate available for ID3Tag, using fallback")
      }

      this.log("Passed Tag: ", id3Tag)

      let frameDuration = parseFloat(id3Tag.full_dur_frames || "0")
      let duration = frameDuration === 0 ? 86400 : Math.round(frameDuration / 25)

      var content_metadata_object = {

        // SDK Metadata
        type:           'content',
        assetid:        id3Tag.pcode || "",
        program:        decode(formattedTitleForTag(id3Tag)).slice(0, 100),
        title:          decode(id3Tag.title || "").slice(0, 100),
        length:         duration.toString(), // length in seconds (String)
        airdate:        airDateFormatted,
        isfullepisode:  'y',
        adloadtype:     '1',
        segB:           country_code || '',
        segC:           'TG4 Player', // optional
      }


      this.log("Loading Nielsen: ", content_metadata_object)
      nSdkInstance.ggPM("loadMetadata", content_metadata_object);

    } catch(err) {
      console.error(err)
    }

  }


  private loggingEnabled = process.env.NODE_ENV !== 'production';
  private log(...data: any[]) {
    if (this.loggingEnabled) {
      console.log("NIEL: ", ...data)
    }
  }

}

export default new NielsenPlugin() ;
